/* eslint-disable camelcase */
import React, { useEffect, useState } from "react";
import {
    JobCard,
    JobCardProps,
    JobSearchData,
    JobSearchLocation,
} from "@themuse/design-system";
import {
    SnowplowClickEventProps, SnowplowTileContentProps, triggerSnowplowClickEvent
} from "@themuse/snowplow-js-client";
import { formatCardDate } from "utils/strings";
import { genJobTrackingProps } from "utils/tracking";
import styles from "./JobCardListItem.module.scss";

/**
 * Generate the apply now URL for a job tile based on the job search data.
 */
const getJobTileApplyUrl = (job: JobSearchData): string => {
    const {
        apply_type,
        company: urlCompany,
        legacy_id,
        short_title
    } = job;
    const { short_name } = urlCompany;
    return apply_type === "external"
        ? `/job/redirect/${legacy_id}`
        : `/jobs/${short_name}/${short_title}#apply-inline-form`;
};

interface GetJobTileLocationProps {
    locationType: string;
    locations: JobSearchLocation[];
    sessionDma: string;
}

/**
 * Determine the location to display for a job tile based on the user's location and the job's location information.
 */
const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        locationType,
        locations,
        sessionDma
    } = props;
    const isRemote: boolean = locationType === "virtual";
    const userLocation: string = sessionDma;
    // We Only care about the actual location address.
    const locationStrings: string[] = locations.map((location) => location.address);
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.filter((loc) => loc === userLocation)[0];
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.filter((address) => getState(address) === getState(userLocation))[0];
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

interface IHandleCardClick {
    viewJobHref: string;
    tracking: SnowplowClickEventProps & SnowplowTileContentProps;
}

/**
 * Generate a click event handler for a JobCardListItem component that opens a new window with a new URL
 * and triggers a Snowplow click event if the clicked element is not an anchor element.
 */
const generateHandleCardClick = (props: IHandleCardClick) => (event?: React.SyntheticEvent<HTMLElement, Event>) => {
    const { viewJobHref, tracking } = props;
    // Only fire on clicking non anchor elements
    if (!(event.target instanceof HTMLAnchorElement)) {
        window.open(viewJobHref, "_blank");
        triggerSnowplowClickEvent(tracking);
    }
};

/**
 * Generate a function that triggers a Snowplow click evens when a link is clicked.
 */
const generateHandleLinkClick = (props: IHandleCardClick) => () => {
    const { tracking } = props;
    triggerSnowplowClickEvent(tracking);
};

/**
 * A React component that renders a JobCard with specific props and tracking events.
 */
const JobCardListItem = (props: {
    job: { hit: JobSearchData; },
    userLocationDma: string,
    order: number
}): React.ReactElement => {
    const {
        job, userLocationDma, order
    } = props;
    const { hit } = job;
    const {
        company: searchResultsCompany,
        id,
        snowplow_id,
        legacy_id,
        locations,
        posted_at,
        remote_work_location_type,
        short_title,
        title,
    } = hit;
    const applyNowHref = getJobTileApplyUrl(hit);
    const companyHref = `/profiles/${searchResultsCompany.short_name}`;
    const viewJobHref = `/jobs/${searchResultsCompany.short_name}/${short_title}`;
    const [postedDate, setPostedDate] = useState("");

    useEffect(() => {
        setPostedDate(formatCardDate(new Date(posted_at * 1000)));
    }, [posted_at]);

    const tileLocation = getJobTileLocation({
        locationType: remote_work_location_type,
        locations,
        sessionDma: userLocationDma || ""
    });

    const snowplowTileContentProps: SnowplowTileContentProps = {
        sp_tile_type: "job",
        sp_tile_company_id: searchResultsCompany.legacy_id.toString(),
        sp_tile_entity_id: legacy_id.toString(),
        sp_tile_entity_uuid: snowplow_id || id,
        sp_tile_location: "job_section",
        sp_tile_order: order + 1
    };

    const snowplowClickBaseProps = {
        sp_click_component: "tile",
        sp_click_component_tile_type: "job",
        sp_click_position: "main",
        sp_click_target_page_section: "job_detail",
        sp_click_search_id: "" // TODO: should be searchId, but I cannot find it.
    };

    const handleCardClick = generateHandleCardClick({
        viewJobHref,
        tracking: {
            ...snowplowTileContentProps,
            ...snowplowClickBaseProps,
            sp_click_target_page_section: "job_detail"
        }
    });

    const handleCompanyNameClick = generateHandleLinkClick({
        viewJobHref,
        tracking: {
            ...snowplowTileContentProps,
            ...snowplowClickBaseProps,
            sp_click_component: "button",
            sp_click_button_type: "profile",
            sp_click_target_page_section: "profile",
        }
    });

    const handleViewJobClick = generateHandleLinkClick({
        viewJobHref,
        tracking: {
            ...snowplowTileContentProps,
            ...snowplowClickBaseProps,
            sp_click_component: "button",
            sp_click_button_type: "job_view",
            sp_click_button_text: "VIEW JOB",
            sp_click_target_page_section: "profile",
        }
    });

    // Apply on company site
    const handleApplyNowClick = generateHandleLinkClick({
        viewJobHref,
        tracking: {
            ...snowplowTileContentProps,
            ...snowplowClickBaseProps,
            sp_click_component: "button",
            sp_click_button_type: "apply",
            sp_click_button_text: "APPLY ON COMPANY SITE",
            sp_click_target_page_section: "off_site",
        }
    });

    const jobCardProps: JobCardProps = {
        showLogo: true,
        companyName: searchResultsCompany.name,
        companyLogo: searchResultsCompany.logo,
        jobName: title,
        locations: tileLocation,
        companyHref,
        applyNowHref,
        postedDate,
        viewJobHref,
        onApplyNowClick: handleApplyNowClick,
        onViewJobClick: handleViewJobClick,
        onCompanyNameClick: handleCompanyNameClick,
        onCardClick: handleCardClick,
        heading: "h3",
        companyIsLink: false
    };

    return (
        <li
            className={styles.jobCardListItem}
            key={job?.hit?.id}
            data-tracking-event-type="impression"
            data-tracking-props={genJobTrackingProps(job, order)}
        >
            <JobCard {...jobCardProps} />
        </li>
    );
};
JobCardListItem.displayName = "JobCardListItem";

export { JobCardListItem };
